<template>
  <div ref="publisherRef" class="publisher-select">
    <a-select
      style="width: 100%"
      v-model:value="selectedPublisherId"
      :placeholder="placeholder"
      :getPopupContainer="getPopupContainer"
      :show-search="true"
      :virtual="true"
      :allowClear="true"
      :not-found-content="!loadingPublisher ? 'No data found' : 'Loading...'"
      :filter-option="false"
      @search="handleSearchPublisher"
      @popupScroll="handlePopupScrollPublisher"
    >
      <a-select-option
        v-for="item in publisherList"
        :key="item.value"
        :value="item.value"
      >
        {{ item.label }}
      </a-select-option>
    </a-select>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { message } from 'ant-design-vue'
import { publishersApi } from '@/server'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Select a publisher',
  },
  /** When false, skip sessionStorage `publiserName` preselect (e.g. CPC list filter). */
  syncSessionPublisherName: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:modelValue'])

const publisherRef = ref(null)
const publisherList = ref([])
const loadingPublisher = ref(false)
const hasMorePublisher = ref(true)
const paginationPublisher = reactive({
  size: 10,
  current: 1,
})
const searchValuePublisher = ref('')

const selectedPublisherId = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const getPopupContainer = () => publisherRef.value || document.body

const handleSearchPublisher = (value, isInit = false) => {
  searchValuePublisher.value = value
  paginationPublisher.current = 1
  publisherList.value = []
  hasMorePublisher.value = true
  getPublisherList(1, value, isInit)
}

const handlePopupScrollPublisher = (e) => {
  const { target } = e
  const scrollHeight = target.scrollHeight
  const scrollTop = target.scrollTop
  const clientHeight = target.clientHeight

  if (
    scrollHeight - scrollTop - clientHeight < 100 &&
    hasMorePublisher.value &&
    !loadingPublisher.value
  ) {
    getPublisherList(
      paginationPublisher.current + 1,
      searchValuePublisher.value,
    )
  }
}

const getPublisherList = async (page, searchText, isInit) => {
  if (!hasMorePublisher.value || loadingPublisher.value) return
  loadingPublisher.value = true
  try {
    const data = await publishersApi.getPublishers({
      page: paginationPublisher.current,
      size: paginationPublisher.size,
      associateEnable: '1',
      keyword: searchText,
    })

    if (data.success) {
      const newPublisherList = data.data.data.map((item) => ({
        label: item.publisherVO?.creatorName,
        value: item.publisherVO?.id,
      }))

      if (page === 1) {
        publisherList.value = newPublisherList
      } else {
        publisherList.value = [...publisherList.value, ...newPublisherList]
      }
      if (isInit) {
        const matched = publisherList.value.find(
          (item) => item.label === sessionStorage.getItem('publiserName'),
        )?.value
        if (matched !== undefined) {
          selectedPublisherId.value = matched
        }
      }
      hasMorePublisher.value = newPublisherList.length !== data.data.total
      paginationPublisher.current = page
    } else if (page === 1) {
      publisherList.value = []
    }
    publisherList.value.unshift({
      label: 'All',
      value: '',
    })
  } catch (error) {
    message.error('Failed to fetch publisher list')
  } finally {
    loadingPublisher.value = false
  }
}

onMounted(() => {
  if (
    props.syncSessionPublisherName &&
    sessionStorage.getItem('publiserName')
  ) {
    handleSearchPublisher(sessionStorage.getItem('publiserName'), true)
    getPublisherList(1, searchValuePublisher.value)
  } else {
    getPublisherList(1, searchValuePublisher.value)
  }
})
</script>
